import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SEO } from '../../../components/SEO';
import {
  ArrowLeft, MapPin, ExternalLink, ChevronDown,
  Map, Wand2, Navigation, RefreshCw, Database,
  Shield, BarChart2, Settings,
} from 'lucide-react';

/* â”€â”€â”€ Data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const features = [
  {
    id: 1, icon: Map, color: '#1A73E8', bg: '#EBF4FF',
    title: 'Interactive Map at Checkout',
    desc: 'A live Google Map appears right above the delivery address step. Customers tap anywhere to drop a pin — no app switching, no copy-pasting coordinates.',
  },
  {
    id: 2, icon: Wand2, color: '#7C3AED', bg: '#F3EEFF',
    title: 'Smart Address Auto-Fill',
    desc: 'Once a pin is dropped, Mapify reverse-geocodes the coordinates and automatically fills in the street, city, zip, and country fields. Zero typing required.',
  },
  {
    id: 3, icon: Navigation, color: '#059669', bg: '#ECFDF5',
    title: 'Geolocation Detection',
    desc: "On load, Mapify requests the browser's location and centers the map on the customer automatically. One tap to confirm — done.",
  },
  {
    id: 4, icon: RefreshCw, color: '#D97706', bg: '#FFFBEB',
    title: 'Session Persistence',
    desc: "If a customer leaves and comes back, their previously pinned location is restored instantly from saved metafields. No re-pinning needed.",
  },
  {
    id: 5, icon: Database, color: '#DC2626', bg: '#FEF2F2',
    title: 'Location Saved to Every Order',
    desc: 'Latitude, longitude, formatted address, and a direct Google Maps link are stored as order metafields. Merchants always know the precise drop point.',
  },
  {
    id: 6, icon: Shield, color: '#0891B2', bg: '#EFF6FF',
    title: 'Graceful & Non-Blocking',
    desc: "The map never interrupts checkout. If the customer skips it, the order still goes through. If the map fails to load, checkout is unaffected.",
  },
  {
    id: 7, icon: BarChart2, color: '#7C3AED', bg: '#F3EEFF',
    title: 'Usage-Based Billing',
    desc: "Merchants only pay for what they use. Every geocoding call is metered at a transparent per-call rate with a billing dashboard built into the app.",
  },
  {
    id: 8, icon: Settings, color: '#059669', bg: '#ECFDF5',
    title: 'Easy Setup',
    desc: "One proxy URL in the Checkout Editor, optional default map center coordinates, and an auto-fill toggle. Live in minutes.",
  },
];

/* â”€â”€â”€ Shared dark section bg â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const DarkBg = ({ children, className = '' }) => (
  <section className={`relative overflow-hidden ${className}`} style={{ background: '#0B1426' }}>
    {/* Grid lines */}
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      backgroundImage: 'linear-gradient(rgba(26,115,232,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(26,115,232,0.07) 1px, transparent 1px)',
      backgroundSize: '48px 48px',
    }} />
    {/* Orbs */}
    <div style={{ position: 'absolute', top: '-20%', right: '-10%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(26,115,232,0.14) 0%, transparent 70%)', pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', bottom: '-15%', left: '-5%', width: 420, height: 420, background: 'radial-gradient(circle, rgba(52,168,83,0.09) 0%, transparent 70%)', pointerEvents: 'none' }} />
    <div className="relative z-10">{children}</div>
  </section>
);

/* â”€â”€â”€ Mapify Top Bar â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const MapifyTopBar = () => (
  <div
    className="sticky top-0 z-50 border-b border-[#E2E8F0]"
    style={{ background: 'rgba(248,247,244,0.95)', backdropFilter: 'blur(20px)' }}
  >
    <div className="container-custom py-4 flex items-center justify-between">
      <Link
        to="/"
        className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
        Back to site
      </Link>

      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ backgroundColor: '#1A73E8' }}>
          <MapPin size={15} color="#fff" />
        </div>
        <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Mapify</span>
      </div>

      <a
        href="#"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#95BF47' }}
      >
        Add to Shopify
        <ExternalLink size={12} />
      </a>
    </div>
  </div>
);

/* â”€â”€â”€ Footer Strip â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const MapifyFooter = () => (
  <div className="border-t border-[#E2E8F0] py-6">
    <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
        © {new Date().getFullYear()} Mapify · A Smart Solutions Product
      </p>
      <div className="flex items-center gap-4">
        <Link to="/apps/mapify/terms" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Terms</Link>
        <span className="text-[#E2E8F0]">·</span>
        <Link to="/apps/mapify/privacy" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Privacy</Link>
      </div>
    </div>
  </div>
);

/* â”€â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export const MapifyFeaturesPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] flex flex-col">
      <SEO
        title="Mapify — Google Maps Pin Location for Shopify Checkout"
        description="Mapify adds an interactive Google Map at Shopify checkout so customers drop a pin to set their delivery location. Auto-fills addresses, reduces failed deliveries, and improves last-mile accuracy."
        path="/apps/mapify"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Mapify',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Shopify',
          description: 'Mapify is a Shopify app that adds an interactive Google Map at checkout. Customers drop a pin on their exact delivery location and the address is auto-filled by reverse geocoding.',
          offers: {
            '@type': 'Offer',
            priceCurrency: 'USD',
            price: '0',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Smart Solutions',
            url: 'https://smartsolution.ai',
          },
        }}
      />
      <MapifyTopBar />

      {/* â”€â”€ HERO â”€â”€ */}
      <DarkBg>
        <div className="container-custom py-28 md:py-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            {/* Badge */}
            <div className="flex items-center gap-3 mb-8">
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 border text-[0.65rem] font-mono tracking-widest uppercase"
                style={{ borderColor: 'rgba(26,115,232,0.35)', color: '#1A73E8', backgroundColor: 'rgba(26,115,232,0.08)' }}
              >
                <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ backgroundColor: '#1A73E8' }} />
                Shopify Checkout Extension
              </span>
              <span className="w-6 h-px" style={{ backgroundColor: 'rgba(26,115,232,0.3)' }} />
            </div>

            {/* Headline */}
            <h1
              className="font-display font-bold text-white mb-6 leading-[1.05]"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
            >
              Every Delivery,{' '}
              <span className="italic" style={{ color: '#1A73E8' }}>Pinned</span>{' '}
              Precisely.
            </h1>

            {/* Subtitle */}
            <p
              className="text-[#94A3B8] leading-relaxed mb-10 max-w-xl"
              style={{ fontSize: 'clamp(1rem, 2vw, 1.125rem)' }}
            >
              Mapify embeds an interactive Google Map directly into the Shopify checkout flow.
              Customers pin their exact drop point — Mapify converts it to a formatted address
              and saves it to the order as metafields.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 mb-16">
              <a
                href="#"
                className="inline-flex items-center gap-2.5 px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#95BF47' }}
              >
                <img src="/resources/shopify.png" className="w-4 h-4 object-contain" alt="Shopify" />
                Add to Shopify
              </a>
              <a
                href="#features"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border transition-colors hover:border-white/40 hover:text-white"
                style={{ borderColor: 'rgba(148,163,184,0.4)', color: '#94A3B8' }}
              >
                Explore Features
                <ChevronDown size={14} />
              </a>
            </div>

            {/* Stats */}
            <div
              className="grid grid-cols-3 gap-8 pt-8"
              style={{ borderTop: '1px solid rgba(148,163,184,0.15)' }}
            >
              {[
                { num: '8', label: 'Core Features' },
                { num: '< 5 min', label: 'Setup Time' },
                { num: 'Pay-as-you-go', label: 'Billing Model' },
              ].map(({ num, label }) => (
                <div key={label}>
                  <div
                    className="font-display font-bold text-white mb-1"
                    style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.6rem)' }}
                  >
                    {num}
                  </div>
                  <div className="font-mono text-[0.6rem] tracking-widest text-[#64748B] uppercase">{label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom fade */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 80,
          background: 'linear-gradient(to bottom, transparent, #F8F7F4)',
          pointerEvents: 'none',
        }} />
      </DarkBg>

      {/* â”€â”€ FEATURES â”€â”€ */}
      <section id="features" className="section-padding bg-[#F8F7F4] flex-1">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">What Mapify Does</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h2
              className="font-display font-bold text-[#0F172A]"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              Built for{' '}
              <span className="italic text-gold">precision</span>{' '}
              delivery.
            </h2>
            <p className="text-[#374151] max-w-md mt-4 text-sm leading-relaxed">
              Eight features working together to give merchants and customers a seamless,
              location-aware checkout experience.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {features.map((feat, i) => (
              <motion.div
                key={feat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-white border border-[#E2E8F0] p-8 group hover:border-[#C7D2FE] transition-all duration-300"
                style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
              >
                <div className="flex items-start gap-5">
                  <div
                    className="w-12 h-12 flex items-center justify-center shrink-0 border border-[#E2E8F0]"
                    style={{ backgroundColor: feat.bg }}
                  >
                    <feat.icon size={22} style={{ color: feat.color }} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2 group-hover:text-[#2563EB] transition-colors duration-300">
                      {feat.title}
                    </h3>
                    <p className="text-[#374151] text-sm leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* â”€â”€ CTA â”€â”€ */}
      <DarkBg className="py-24">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="w-6 h-px" style={{ backgroundColor: 'rgba(26,115,232,0.4)' }} />
              <span
                className="font-mono text-[0.65rem] tracking-[0.25em] uppercase"
                style={{ color: '#1A73E8' }}
              >
                Get Started
              </span>
              <span className="w-6 h-px" style={{ backgroundColor: 'rgba(26,115,232,0.4)' }} />
            </div>

            <h2
              className="font-display font-bold text-white mb-6 leading-[1.1]"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }}
            >
              Ready to pin your{' '}
              <span className="italic" style={{ color: '#1A73E8' }}>first delivery</span>?
            </h2>

            <p className="text-[#94A3B8] mb-10 leading-relaxed max-w-lg mx-auto">
              Join merchants using Mapify to give customers the most precise checkout experience on Shopify.
            </p>

            <a
              href="#"
              className="inline-flex items-center gap-2.5 px-8 py-4 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#95BF47' }}
            >
              <img src="/resources/shopify.png" className="w-4 h-4 object-contain" alt="Shopify" />
              Add to Shopify — It's Free to Try
            </a>
          </motion.div>
        </div>
      </DarkBg>

      <MapifyFooter />
    </div>
  );
};
